import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { Observable, switchMap } from 'rxjs';
import { OfflineFormsClient } from '../../offline-forms.client';

@Component({
  selector: 'lfw-index-db-size-dialog',
  templateUrl: './index-db-size-dialog.component.html',
  styleUrls: ['./index-db-size-dialog.component.css'],
  imports: [NgxIndexedDBModule, MatDialogModule, AsyncPipe, MatButtonModule, TranslatePipe]
})
export class IndexDbSizeDialogComponent {
  storageFacts$: Observable<StorageFacts>;
  readonly dialogRef = inject(MatDialogRef<IndexDbSizeDialogComponent>);
  private readonly offlineFormsClient = inject(OfflineFormsClient);

  constructor() {
    this.storageFacts$ = this.offlineFormsClient.offlineOrders$.pipe(
      takeUntilDestroyed(),
      switchMap(async () => {
        const estimate = await navigator.storage.estimate();
        // calculate remaining storage in MB
        return {
          availableSpace: this.humanReadable((estimate.quota || 0) - (estimate.usage || 0)),
          usedSpace: this.humanReadable(estimate.usage || 0),
          temporaryOrdersCount: (await this.offlineFormsClient.temporaryOfflineOrdersCount()) || 0,
          offlineOrdersCount: (await this.offlineFormsClient.offlineOrdersCount()) || 0
        };
      })
    );
  }

  private humanReadable(size: number): string {
    const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
    return +(size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
  }
}

interface StorageFacts {
  availableSpace: string;
  temporaryOrdersCount: number;
  offlineOrdersCount: number;
  usedSpace: string;
}
